import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { CHAT_PRESETS, EMOTES, type GameResultPayload, type PublicUserDto, type SeatPublic, type TurnInfo } from '@cardhub/protocol';
import { Avatar, Coins, Confetti, CountUp, Icon, Modal } from '../components/ui';
import { useT } from '../i18n';
import { useCountdown } from '../lib/hooks';
import { useGame } from '../store/game';
import { useSession } from '../store/session';
import { ringSlots } from './ring';

/**
 * Seat slots (% of the table stage) for `k` opponents, clockwise from the player's left: a column on each
 * side plus a row across the top. Everything stays in the upper ~55 % of the stage so the trick / board /
 * pot below the middle and the player's own dock never collide with an opponent.
 */
function opponentSlots(k: number): { x: number; y: number; dir: 'left' | 'top' | 'right' }[] {
  const side = k <= 1 ? 0 : k <= 5 ? 1 : k <= 7 ? 2 : 3;
  const top = k - 2 * side;
  const sideY = side === 1 ? (top === 0 ? [22] : [28]) : side === 2 ? [40, 13] : [46, 28, 10];
  const topX = top === 1 ? [50] : top === 2 ? (side ? [34, 66] : [30, 70]) : top === 3 ? [27, 50, 73] : Array.from({ length: top }, (_, i) => 18 + (64 * i) / Math.max(1, top - 1));
  return [
    ...sideY.slice(0, side).map((y) => ({ x: 0, y, dir: 'left' as const })),
    ...topX.map((x) => ({ x, y: 2, dir: 'top' as const })),
    ...[...sideY.slice(0, side)].reverse().map((y) => ({ x: 100, y, dir: 'right' as const })),
  ];
}

/** Position of a seat relative to the local player (who is at the bottom, in the dock). */
export function seatPosition(seat: number, mySeat: number | null, total: number): { x: number; y: number; dir: 'bottom' | 'left' | 'top' | 'right' } {
  // Spectators have no seat of their own: every player goes into the opponent slots.
  if (mySeat === null) return opponentSlots(total)[seat]!;
  const rel = ((seat - mySeat) % total + total) % total;
  if (rel === 0) return { x: 50, y: 90, dir: 'bottom' };
  return opponentSlots(total - 1)[rel - 1]!;
}

/** True on phone-width screens; table components use smaller cards there. */
export function useNarrow(limit = 480): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < limit);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < limit);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [limit]);
  return narrow;
}

export function TurnRing({ turn, size }: { turn: TurnInfo | null; size: number }) {
  const left = useCountdown(turn?.deadline);
  if (!turn?.deadline || !turn.turnMs) return null;
  const frac = Math.max(0, Math.min(1, left / turn.turnMs));
  const r = size / 2 + 2;
  const c = 2 * Math.PI * r;
  const color = frac > 0.5 ? 'var(--accent)' : frac > 0.2 ? 'var(--warn)' : 'var(--danger)';
  return (
    <svg className="timer" viewBox={`0 0 ${size + 8} ${size + 8}`} aria-hidden>
      <circle cx={(size + 8) / 2} cy={(size + 8) / 2} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
      <circle cx={(size + 8) / 2} cy={(size + 8) / 2} r={r} fill="none" stroke={color} strokeWidth="4" strokeDasharray={c} strokeDashoffset={c * (1 - frac)} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.2s linear' }} />
    </svg>
  );
}

/** Size of the stage (the seat's positioned parent) in px, kept current on resize; null until measured or while `on` is false. */
function useStageSize(ref: { current: HTMLElement | null }, on: boolean): { w: number; h: number } | null {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const stage = ref.current?.offsetParent as HTMLElement | null | undefined;
    if (!on || !stage) return;
    const read = () => setSize((o) => (o && o.w === stage.clientWidth && o.h === stage.clientHeight ? o : { w: stage.clientWidth, h: stage.clientHeight }));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [ref, on]);
  return on ? size : null;
}

export function SeatView({
  seat,
  info,
  mySeat,
  total,
  turn,
  sub,
  tags,
  children,
  folded,
  speech,
}: {
  seat: number;
  info: SeatPublic | undefined;
  mySeat: number | null;
  total: number;
  turn: TurnInfo | null;
  sub?: ReactNode;
  tags?: ReactNode;
  children?: ReactNode;
  folded?: boolean;
  speech?: string | null;
}) {
  const t = useT();
  // Bot seats look like any other player unless the operator turns the badge on (remote config bots.showBadge).
  const disclose = useSession((s) => (s.boot?.config.bots as { showBadge?: boolean } | undefined)?.showBadge ?? false);
  const narrow = useNarrow();
  let pos = seatPosition(seat, mySeat, total);
  const active = turn?.seats.includes(seat) ?? false;
  const user: PublicUserDto | null = info?.user ?? null;
  const size = narrow ? 38 : 48;
  // Every seat count: spread the seats at equal distances around the table (the local player is the bottom slot).
  const ref = useRef<HTMLDivElement>(null);
  const ringStage = useStageSize(ref, total >= 2);
  const ringIdx = mySeat === null ? seat + 1 : (((seat - mySeat) % total) + total) % total;
  const ring = ringStage ? ringSlots(mySeat === null ? total + 1 : total, ringStage.w, ringStage.h, narrow ? 84 : 116, narrow ? 118 : 122)[ringIdx] : undefined;
  if (ring) pos = { x: 0, y: 0, dir: ring.dir };
  // Keep every seat fully inside the stage: clamp horizontally, anchor top seats by their top edge.
  const place: CSSProperties = ring
    ? { left: ring.x, top: ring.y, transform: 'translate(-50%, -50%)' }
    : {
        left: `clamp(calc(var(--seat-w) / 2 + 2px), ${pos.x}%, calc(100% - var(--seat-w) / 2 - 2px))`,
        // Side seats always start below the top row (its height is --top-row-h), whatever the stage height.
        top: pos.dir === 'top' ? `${pos.y}%` : pos.dir === 'bottom' ? `${pos.y}%` : `max(${pos.y}%, var(--top-row-h))`,
        transform: pos.dir === 'top' ? 'translate(-50%, 0)' : pos.dir === 'bottom' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
      };
  if (!user) {
    return (
      <div ref={ref} className={`seat dir-${pos.dir}`} style={{ ...place, opacity: 0.45 }} aria-label="Empty seat">
        <div className="avatar" style={{ ['--size' as string]: `${size}px`, background: 'var(--surface-2)', borderStyle: 'dashed' }} />
      </div>
    );
  }
  if (seat === mySeat) {
    // Local player's info sits above their hand; keep a compact marker on the felt for the turn ring.
    return null;
  }
  return (
    <div ref={ref} className={`seat dir-${pos.dir} ${active ? 'active' : ''} ${folded ? 'folded' : ''}`} style={place} aria-label={`${user.username}${active ? ', playing now' : ''}`}>
      {speech && <div className="speech">{speech}</div>}
      <div className="ring">
        <Avatar avatar={user.avatar} size={size} name={user.username} />
        {active && <TurnRing turn={turn} size={size} />}
      </div>
      <div className="nameplate">
        <span className="n">{user.username}</span>
        {sub}
      </div>
      <div className="tags">
        {info?.isBot && disclose && <span className="tag bot">{t('common.bot')}</span>}
        {info && !info.isBot && (!info.connected || info.botControlled) && <span className="tag away">{t('table.away')}</span>}
        {tags}
      </div>
      {children}
    </div>
  );
}

/** The local player's strip at the top of the dock: avatar, name, chips/points, turn timer, optional cards. */
export function MyPanel({ seat, sub, tags, aside }: { seat: SeatPublic | undefined; sub?: ReactNode; tags?: ReactNode; aside?: ReactNode }) {
  const turn = useGame((s) => s.turn);
  const mySeat = useGame((s) => s.mySeat);
  const t = useT();
  const left = useCountdown(turn?.deadline);
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  if (!seat?.user) return null;
  return (
    <div className={`my-panel ${myTurn ? 'my-turn' : ''}`}>
      <div className="ring" style={{ position: 'relative', borderRadius: '50%', flex: 'none' }}>
        <Avatar avatar={seat.user.avatar} size={34} name={seat.user.username} />
        {myTurn && <TurnRing turn={turn} size={34} />}
      </div>
      <div className="who">
        <strong className="ellipsis">{seat.user.username}</strong>
        <span className="score">{sub}</span>
        {tags && <div className="tags">{tags}</div>}
        {myTurn && turn?.deadline ? (
          <span className="turn-pill" aria-live="polite">
            {t('table.yourTurn')} · {Math.ceil(left / 1000)}s
          </span>
        ) : null}
      </div>
      {aside && <div className="aside">{aside}</div>}
    </div>
  );
}

/** The local player's strip on the table's bottom edge (their hand and buttons stay in the dock below). */
export function MySeat(props: { seat: SeatPublic | undefined; sub?: ReactNode; tags?: ReactNode }) {
  return (
    <div className="my-seat">
      <MyPanel {...props} />
    </div>
  );
}

/** Latest chat line per seat shown as speech bubbles for a few seconds. */
export function useSpeech(): Record<number, string> {
  const chat = useGame((s) => s.chat);
  const [bubbles, setBubbles] = useState<Record<number, string>>({});
  const last = useRef<string | null>(null);
  useEffect(() => {
    const m = chat.at(-1);
    if (!m || m.id === last.current || m.from.seat === null) return;
    last.current = m.id;
    const text = m.text ?? (m.preset ? presetLabel(m.preset) : m.emote ? EMOTE_EMOJI[m.emote] : '') ?? '';
    setBubbles((b) => ({ ...b, [m.from.seat!]: text }));
    const seat = m.from.seat;
    const tm = setTimeout(() => setBubbles((b) => {
      const n = { ...b };
      delete n[seat];
      return n;
    }), 3500);
    return () => clearTimeout(tm);
  }, [chat]);
  return bubbles;
}

export const EMOTE_EMOJI: Record<string, string> = { laugh: '😂', cool: '😎', angry: '😠', cry: '😢', clap: '👏', fire: '🔥', think: '🤔', heart: '❤️' };
const PRESET_TEXT: Record<string, string> = {
  gg: 'Good game!',
  nice_move: 'Nice move!',
  well_played: 'Well played',
  hurry_up: 'Hurry up 😅',
  oops: 'Oops!',
  thanks: 'Thanks!',
  good_luck: 'Good luck!',
  rematch: 'Rematch?',
};
export const presetLabel = (p: string) => PRESET_TEXT[p] ?? p;

export function ChatPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const chat = useGame((s) => s.chat);
  const send = useGame((s) => s.sendChat);
  const [text, setText] = useState('');
  const log = useRef<HTMLDivElement>(null);
  const chatOn = useSession((s) => s.boot?.flags.CHAT_ENABLED ?? true);
  useEffect(() => {
    log.current?.scrollTo({ top: log.current.scrollHeight });
  }, [chat, open]);
  if (!open) return null;
  return (
    <div className="chat-panel card glass" role="dialog" aria-label={t('table.chat')}>
      <div className="spread">
        <strong>{t('table.chat')}</strong>
        <button className="icon-btn" style={{ width: 36, height: 36 }} onClick={onClose} aria-label={t('common.close')}>
          <Icon name="close" size={18} />
        </button>
      </div>
      <div className="chat-log" ref={log} aria-live="polite">
        {chat.map((m) => (
          <div key={m.id} className="chat-bubble">
            <strong>{m.from.username}: </strong>
            {m.text ?? (m.preset ? presetLabel(m.preset) : EMOTE_EMOJI[m.emote ?? ''])}
          </div>
        ))}
      </div>
      <div className="row wrap" style={{ gap: 6, padding: '6px 0' }}>
        {EMOTES.map((e) => (
          <button key={e} className="icon-btn" style={{ width: 38, height: 38, fontSize: 20 }} onClick={() => void send({ emote: e })} aria-label={e}>
            {EMOTE_EMOJI[e]}
          </button>
        ))}
      </div>
      <div className="row wrap" style={{ gap: 6 }}>
        {CHAT_PRESETS.map((p) => (
          <button key={p} className="pill" onClick={() => void send({ preset: p })}>
            {presetLabel(p)}
          </button>
        ))}
      </div>
      {chatOn && (
        <form
          className="row"
          style={{ marginTop: 8 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) {
              void send({ text: text.trim() });
              setText('');
            }
          }}
        >
          <input className="input" value={text} maxLength={200} onChange={(e) => setText(e.target.value)} placeholder={t('table.chat')} aria-label={t('table.chat')} />
          <button className="btn primary sm">{t('table.send')}</button>
        </form>
      )}
    </div>
  );
}

export function MatchResults({ result, onPlayAgain, onLobby }: { result: GameResultPayload; onPlayAgain: () => void; onLobby: () => void }) {
  const t = useT();
  const me = result.me;
  const won = (me?.coinsDelta ?? 0) > 0;
  return (
    <Modal open onClose={onLobby} title={t('results.title')}>
      {me?.place === 1 && <Confetti />}
      <div className="col" style={{ alignItems: 'center', textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 52 }} aria-hidden className={me?.place === 1 ? 'winner-glow' : ''}>
          {me?.place === 1 ? '🏆' : me?.place === 2 ? '🥈' : me?.place === 3 ? '🥉' : '🃏'}
        </div>
        {me && (
          <>
            <strong style={{ fontSize: 'var(--fs-xl)' }}>{won ? t('results.won', { n: me.coinsDelta }) : t('results.lost', { n: Math.abs(me.coinsDelta) })}</strong>
            <div className="row" style={{ justifyContent: 'center' }}>
              <span className="pill accent">
                +<CountUp to={me.xpGained} /> XP
              </span>
              {me.ratingDelta !== null && <span className="pill">{t('results.rating', { r: me.rating ?? 0, d: (me.ratingDelta >= 0 ? '+' : '') + me.ratingDelta })}</span>}
            </div>
            {me.leveledUp && <span className="pill gold">⭐ {t('results.levelUp', { n: me.level })}</span>}
          </>
        )}
      </div>
      <div className="list">
        {result.standings.map((s) => (
          <div key={s.seat} className="list-item" style={s.seat === me?.seat ? { background: 'rgba(34,211,166,0.12)' } : undefined}>
            <strong className="num" style={{ width: 28 }}>#{s.place}</strong>
            {s.user && <Avatar avatar={s.user.avatar} size={32} />}
            <span className="grow ellipsis">{s.user?.username ?? '—'}</span>
            <span className="num dim">{result.gameType === 'CALL_BREAK' ? (s.score / 10).toFixed(1) : s.score}</span>
          </div>
        ))}
      </div>
      <div className="row" style={{ marginTop: 16 }}>
        <button className="btn ghost grow" onClick={onLobby}>
          {t('results.lobby')}
        </button>
        <button className="btn primary grow" onClick={onPlayAgain}>
          {t('results.playAgain')}
        </button>
      </div>
    </Modal>
  );
}

/** Small, non-blocking hand-result toast for session games (Teen Patti, Poker, Marriage). */
export function HandResultFlash({ result }: { result: GameResultPayload | null }) {
  const [show, setShow] = useState<GameResultPayload | null>(null);
  useEffect(() => {
    if (!result?.me) return;
    setShow(result);
    const tm = setTimeout(() => setShow(null), 3200);
    return () => clearTimeout(tm);
  }, [result]);
  if (!show?.me) return null;
  const d = show.me.coinsDelta;
  return (
    <div className="toast" style={{ position: 'absolute', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 30, animation: 'pop-in var(--dur-3) var(--ease)' }} role="status">
      <span style={{ fontSize: 22 }}>{d > 0 ? '🎉' : d < 0 ? '😬' : '🤝'}</span>
      <Coins amount={d} signed />
      <span className="pill accent">+{show.me.xpGained} XP</span>
    </div>
  );
}
